import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Show } from '../types/Show';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { useLiveShowContext } from '../contexts/LiveShowContext';
import { renderSubTitleIfValid } from '../utils/subTitleUtils';
import OptimizedShowImage from './common/OptimizedShowImage';
import fallbackShowImage from '../assets/images/e-logo-600-min.png';
import logoImage from '../assets/logos/e-logo-trans-40.png';

interface UpNextShowTileProps {
  show: Show;
  isNewShow?: boolean;
  /**
   * Indicates the tile sits above the fold so we can preload aggressively.
   */
  isPriority?: boolean;
  datePrefix?: string;
}

const UpNextShowTile: React.FC<UpNextShowTileProps> = ({ show, isNewShow = false, isPriority = false, datePrefix }) => {
  const { currentTrack, isPlaying, playShow } = useAudioPlayer();
  const { isShowCurrentlyLive } = useLiveShowContext();
  const navigate = useNavigate();

  // Determine if this show is currently live using the context
  const isCurrentlyLive = isShowCurrentlyLive(show);

  const formatTimeRange = (dateTimeString: string, endDateTimeString?: string) => {
    const [date, time] = dateTimeString.split("#");
    const startTime = new Date(`${date}T${time}`);
    
    const formatTime = (date: Date) => {
      return date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    };

    // Use actual end time from API if available
    if (endDateTimeString) {
      const [endDate, endTime] = endDateTimeString.split("#");
      const endTimeObj = new Date(`${endDate}T${endTime}`);
      return `${formatTime(startTime)} - ${formatTime(endTimeObj)}`;
    }
    
    // Fallback: Determine show duration based on start time patterns
    // Most shows are either 1 or 2 hours, default to 2 hours
    const hour = startTime.getHours();
    let durationHours = 2;
    
    // Common 1-hour show slots (news, short programs)
    if (hour === 12 || hour === 18 || hour === 6) {
      durationHours = 1;
    }
    
    const endTime = new Date(startTime.getTime() + (durationHours * 60 * 60 * 1000));
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  const handlePlayShow = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (show.audioUrl) {
      playShow(show);
    }
  };

  const handleNavigate = useCallback(() => {
    navigate(`/show/${show.show_id}`);
  }, [navigate, show.show_id]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleNavigate();
    }
  }, [handleNavigate]);

  const timeRange = formatTimeRange(show.date_start_time, show.date_end_time);
  // const genres = show.presenter.genres.split(", "); // Removed - no longer needed for hover state

  // Check if this show is currently playing
  const isCurrentlyPlaying =
    currentTrack?.show?.show === show.show &&
    currentTrack?.show?.date_start_time === show.date_start_time &&
    isPlaying;

  const displayTime = datePrefix ? `${datePrefix} ${timeRange}` : timeRange;

  return (
    <div
      className={`group ${isNewShow ? "animate-slide-in-bounce" : ""}`}
      role="button"
      tabIndex={0}
      onClick={handleNavigate}
      onKeyDown={handleKeyDown}
    >
      {/* New show indicator */}
      {isNewShow && (
        <div className="absolute -top-2 -right-2 z-30">
          <div className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center animate-pulse">
            <div className="w-2 h-2 bg-white rounded-full mr-1 animate-ping"></div>
            <span>NEW</span>
          </div>
        </div>
      )}

      <div
        className={`relative block aspect-square rounded-xl sm:rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${
          isNewShow ? "ring-2 ring-green-500 ring-opacity-50" : ""
        }`}
      >
        <div className="absolute inset-0">
          <OptimizedShowImage
            show={show}
            size={isPriority ? 'medium' : 'small'}
            className="w-full h-full"
            alt={show.show}
            priority={isPriority}
            preload={isPriority}
            preconnect={isPriority}
            fallbackSrc={fallbackShowImage}
            preferredVariants={['300x300', '150x150', '400x400']}
            sizes="189px"
          />
        </div>

        {/* Logo bar to match Recent Shows tiles */}
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 via-black/40 to-transparent p-2 sm:p-4 z-20 flex">
          <img
            src={logoImage}
            alt="Eruption Radio"
            className="w-5 h-5 sm:w-6 sm:h-6 object-contain opacity-90 hover:opacity-100 transition-opacity"
            loading="lazy"
          />
        </div>

        {/* Live indicator */}
        {isCurrentlyLive && (
          <div className="absolute top-2 left-2 sm:top-3 sm:left-3 z-20">
            <div className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full flex items-center">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full mr-1 animate-pulse"></div>
              <span className="hidden sm:inline">LIVE NOW</span>
              <span className="sm:hidden">LIVE</span>
            </div>
          </div>
        )}

        {/* Play button overlay */}
        {show.audioUrl && (
          <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center opacity-0 hover:opacity-100">
            <button
              onClick={handlePlayShow}
              className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all duration-200 transform hover:scale-110 ${
                isCurrentlyPlaying
                  ? "bg-red-500 text-white"
                  : "bg-white bg-opacity-90 text-black hover:bg-opacity-100"
              }`}
              aria-label={isCurrentlyPlaying ? "Currently playing" : "Play show"}
            >
              {isCurrentlyPlaying ? (
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 sm:w-6 sm:h-6 ml-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
          </div>
        )}

        {/* Hover overlay to mimic Recent Shows animation */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center pointer-events-none">
          <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 hidden sm:block">
            <div className="bg-white/90 backdrop-blur-sm text-gray-900 px-3 py-1.5 rounded-full text-sm font-medium shadow-lg flex items-center">
              <span>View Details</span>
              <svg className="w-4 h-4 ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Show name overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-2 sm:p-4">
          <h3 className="text-white font-bold text-sm sm:text-lg mb-0.5 sm:mb-1 leading-tight">
            {show.show}
          </h3>
          {/* Sub-title (replaces old show title) - with graceful null/empty handling */}
          {renderSubTitleIfValid(show.sub_title, (validSubTitle) => (
            <p className="text-white text-xs sm:text-sm leading-tight font-medium">{validSubTitle}</p>
          ))}
        </div>

        {/* Genre tags - REMOVED - visible on hover */}
        {/* Genre tags removed to keep hover state clean */}
      </div>

      {/* Prominent time range below the tile */}
      <div className="mt-1 sm:mt-2 text-center">
        <p className="text-white font-bold text-sm sm:text-base">
          {displayTime}
        </p>
      </div>
    </div>
  );
};

export default UpNextShowTile;
